import React, { useCallback, useEffect, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import { doRequest } from '@r1-frontend/do-request';
import { backCall } from '@r1-frontend/api-domru/core-requests/api/v1/call/back';
import { ICurrentCity } from '@r1-frontend/entities/City/models/ICurrentCity';

import { btnTypes } from '@r1-frontend/ui-react/components/buttons/baseButton';
import { FlexBoxCol } from '@r1-frontend/ui-react/components/layouts/flexBoxGrid';
import { PolicyNote } from '@r1-frontend/ui-react/components/PolicyNote';
import { EResultPopupStatus } from '@r1-frontend/ui-react/components/popups/popupResult';
import { IndentContainer } from '@r1-frontend/ui-react/experimental/containers';
import { ACTIONS } from '@r1-frontend/shared/constants/analytics';

import PopupResult from '~/src/components/Popups/PopupResult';
import { POLICY_PAGE_LINK } from '~/src/constants';
import type { TDispatch } from '~/src/features/center/BlockInfo';
import CustomTagRow from '~/src/features/center/hocs/CustomTagRow';
import { useEffectActive } from '~/src/features/center/hooks/useEffectActive';
import { dataLayerForCBPhonePush } from '~/src/helpers/eventsForDataLayer';
import { TState } from '~/src/store';
import selectProviderId from '~/src/store/selectors/selectProviderId';

import * as ST from './styled';

type TField = {
    value: string,
    isValid: boolean,
    clearInput: () => void,
}

interface IProps {
    city: ICurrentCity,
    formProblemRef?: React.RefObject<HTMLDivElement>,
    setActiveForm?: TDispatch,
    withInputName: boolean,
    withLegalText: boolean,
    textButton: string,
    label: string,
}

const Form: React.FC<IProps> = ({
    city,
    formProblemRef,
    setActiveForm,
    withInputName,
    textButton,
    withLegalText,
    label,
}) => {
    const initValueInput = {
        value: '',
        isValid: false,
        clearInput: () => undefined,
    };
    const [name, setName] = useState<TField>(initValueInput);
    const [phone, setPhone] = useState<TField>(initValueInput);
    const [isValid, setIsValid] = useState<boolean>(false);
    const [beenStarted, setDataLayer] = useState<boolean>(false);
    const [hasResult, setHasResult] = useState<boolean>(false);

    const { auth: { isAuth, clientPersonal }, providerId } = useSelector((state: TState) => ({
        auth: state.auth,
        providerId: selectProviderId(state),
    }), shallowEqual);

    useEffectActive(setActiveForm);

    const changeHandler = useCallback((inputData, _setState) => {
        if (!beenStarted && inputData.value) {
            dataLayerForCBPhonePush({
                action: ACTIONS.startedService,
                label,
                uid: isAuth ? clientPersonal?.agreement : null,
            });
            setDataLayer(true);
        }
        _setState(inputData);
    }, [beenStarted, label, isAuth]);

    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    const [message, setMessage] = useState<string | undefined>('');

    const sendAnalytic = useCallback((status: boolean, message?: string | null) => {
        dataLayerForCBPhonePush({
            option: message ? `${label} | ${message}` : label,
            action: status ? ACTIONS.orderService : ACTIONS.errorService,
            uid: isAuth ? clientPersonal?.agreement : null,
        });
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        window._tmr?.push({ id: '3213734', type: 'reachGoal', goal: 'Lead' });
    }, [message]);

    useEffect(() => {
        const isValidWrap = withInputName ? name.isValid && phone.isValid : phone.isValid;
        setIsValid(isValidWrap);
    }, [isValid, name.isValid, phone.isValid, withInputName]);

    const closeHandler = useCallback(() => {
        setHasResult(false);
        setIsValid(false);
    }, []);

    const sendFormHandler = useCallback(async() => {
        setIsLoading(true);
        const resp = await doRequest.coreRequests<unknown>(
            backCall({
                phoneNumber: phone.value,
            }),
        );

        setIsLoading(false);
        if (resp.isSuccess) {
            setIsError(false);
            setMessage('Заявка успешно создана');
        } else {
            setIsError(true);
            setMessage('Заявка не создана');
        }
        setHasResult(true);
        sendAnalytic(resp.isSuccess, !resp.isSuccess ? resp.error.message : undefined);
    }, [providerId, phone, city.cityId]);

    return (
            <ST.Form
                ref={formProblemRef}
            >
                <CustomTagRow
                    withInputName={withInputName}
                >
                    {withInputName
                        ? <ST.ColInput xs={12} md={5} sm={4}>
                            <ST.InputName
                                onChange={(InputVal: string) => changeHandler(InputVal, setName)}
                                type='name'
                                errorMessage='Должны быть только буквы'
                                placeholder='Ваше имя'
                                name='name'
                                className={'ym-disable-keys'}
                            />
                        </ST.ColInput>
                        : <></>
                    }
                    {withLegalText
                        ? <FlexBoxCol xs={12} md={12} sm={12}>
                            <IndentContainer $united={[0, 0, 12, 0]}>
                                <PolicyNote
                                    data-test='agreement'
                                    buttonText={textButton}
                                    agreeUrl={POLICY_PAGE_LINK}
                                />
                            </IndentContainer>
                        </FlexBoxCol>
                        : <></>
                    }
                    <ST.ColInput xs={12} md={5} sm={4}>
                        <ST.InputPhone
                            onChange={(InputVal: string) => changeHandler(InputVal, setPhone)}
                            type='phone'
                            errorMessage='Укажите корректный номер телефона'
                            placeholder='Номер телефона'
                            name='phone'
                            className={'ym-disable-keys'}
                        />
                    </ST.ColInput>
                    <ST.ColButton xs={12} md={3} sm={4}>
                        <ST.Button
                            type={btnTypes.ACCENT}
                            onClick={sendFormHandler}
                            disabled={!isValid || isLoading}
                            isLoading={isLoading}
                        >
                            {textButton}
                        </ST.Button>
                    </ST.ColButton>
                </CustomTagRow>
                <PopupResult
                    isOpen={hasResult}
                    onClose={closeHandler}
                    title={isError ? 'Произошла ошибка' : 'Ваша заявка успешно отправлена'}
                    text={message}
                    type={isError ? EResultPopupStatus.ERROR : EResultPopupStatus.SUCCESS}
                />
            </ST.Form>
    );
};

export default Form;
